import React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import { DICE_LUCK_PERCENTILE_MIN_ROLLS } from '@warcarp/api-types'

/**
 * "How Dice Luck Works" explainer. Shared by the in-game Dice Luck modal and
 * the Hall of Fame. `onShowCalculations` adds the link to the live per-player
 * maths, which only exists in a game.
 */
export function DiceLuckHelp({
  onBack,
  backLabel = 'Back to dice luck stats',
  backGlyph = '←',
  onShowCalculations,
}: {
  onBack: () => void
  backLabel?: string
  backGlyph?: string
  onShowCalculations?: () => void
}) {
  return (
    <ScrollView contentContainerStyle={{ padding: 20, gap: 16 }}>
      <View className="flex-row items-start gap-3">
        <View className="flex-1 gap-1">
          <Text className="font-stencil text-xl uppercase text-ink">How Dice Luck Works</Text>
          <Text className="font-typed text-sm leading-5 text-ink-soft">
            WarCarp compares the armies you lost and defeated with the average
            for those exact dice, border dice included.
          </Text>
        </View>
        <TouchableOpacity
          onPress={onBack}
          accessibilityLabel={backLabel}
          accessibilityRole="button"
          activeOpacity={0.75}
          className="bg-paper border-2 border-olive rounded-full items-center justify-center"
          style={{ width: 38, height: 38 }}
        >
          <Text className="font-stencil text-lg text-ink">{backGlyph}</Text>
        </TouchableOpacity>
      </View>

      <View className="flex-row items-stretch gap-1.5" accessibilityLabel="Possible outcomes lead to expected losses, which are compared with actual losses to produce army swing">
        <View className="flex-1 rounded-lg border border-olive bg-paper-up px-2 py-3 items-center justify-center gap-1">
          <Text className="text-xl">🎲</Text>
          <Text className="font-stencil text-[9px] uppercase text-ink text-center">Possible outcomes</Text>
        </View>
        <Text className="font-stencil text-lg text-olive-up self-center">→</Text>
        <View className="flex-1 rounded-lg border border-olive bg-paper-up px-2 py-3 items-center justify-center gap-1">
          <Text className="text-xl">⚖️</Text>
          <Text className="font-stencil text-[9px] uppercase text-ink text-center">Expected losses</Text>
        </View>
        <Text className="font-stencil text-lg text-olive-up self-center">→</Text>
        <View className="flex-1 rounded-lg border border-olive bg-paper-up px-2 py-3 items-center justify-center gap-1">
          <Text testID="dice-luck-help-swing-icon" className="font-stencil text-xl text-ink">±</Text>
          <Text className="font-stencil text-[9px] uppercase text-ink text-center">Army swing</Text>
        </View>
      </View>

      <View className="rounded-xl border-2 border-olive bg-paper-up px-4 py-3 gap-2">
        <Text className="font-stencil text-xs uppercase tracking-wider text-ink">Example attack</Text>
        <Text className="font-typed text-xs leading-5 text-ink-soft">
          Expected: lose 0.9 armies and defeat 1.1.{`\n`}
          Actual: lose 0 and defeat 2.
        </Text>
        <Text className="font-typed text-sm text-ink">
          (2 − 1.1) + (0.9 − 0) = <Text className="font-stencil text-blood">+1.8 armies</Text>
        </Text>
        <Text className="font-typed text-xs leading-5 text-ink-soft">
          Positive: the dice beat average. Negative: they cost you armies.
        </Text>
      </View>

      <View className="rounded-xl border border-olive bg-paper px-4 py-3 gap-2">
        <Text className="font-stencil text-xs uppercase tracking-wider text-ink">How ranking stays fair</Text>
        <Text className="font-typed text-xs leading-5 text-ink-soft">
          Rank uses a standard score, so more battles don't inflate it:
        </Text>
        <Text className="font-typed text-sm text-ink">
          total swing ÷ √total variance = standard score
        </Text>
        <Text className="font-typed text-xs leading-5 text-ink-soft">
          Luck Score = 100 + 20 × standard score, so 100 is exactly average
          (e.g. +8 ÷ √64 = +1.0 → 120).
        </Text>
        <Text className="font-typed text-xs leading-5 text-ink-soft">
          The rarest 5% at each end are Very lucky or Very unlucky, the middle
          half Average. Very labels and percentiles appear after
          {' '}{DICE_LUCK_PERCENTILE_MIN_ROLLS} rolls (normal approximation).
        </Text>
      </View>

      {onShowCalculations && (
        <TouchableOpacity onPress={onShowCalculations} accessibilityRole="button" accessibilityLabel="See live player calculations">
          <Text className="font-stencil text-xs text-blood">See live player calculations →</Text>
        </TouchableOpacity>
      )}
      <Text className="font-typed text-xs leading-5 text-ink-soft italic">
        Dice only, not strategy or cards. The troop line is context and doesn't
        affect the score.
      </Text>
    </ScrollView>
  )
}
